import { getApiClient } from './client'
import { ApiRequestError } from './types'

import type {
  CheckoutAuthorizationExchangeResponse,
  CheckoutRestartRequest,
  CheckoutRestartResponse,
  CheckoutExecution,
  CheckoutExecutionCancelResponse,
  CheckoutMethod,
  CheckoutResultAuthorizationExchangeResponse,
  CheckoutResultAuthorizationResponse,
  CheckoutPaymentMethodsResponse,
  CheckoutPurchaseSuccessorRequest,
  CheckoutPurchaseSuccessorResponse,
  CheckoutSessionCompleteResponse,
  CheckoutSession,
  CheckoutSessionCancelResponse,
  CheckoutSessionRevisionRequest,
  CheckoutSessionRevisionResponse,
  CheckoutStartExecutionRequest,
  ProductCheckoutResultResponse,
} from './checkout-session-types'

export const CHECKOUT_PRESENTATION_ACCEPT = 'hosted_redirect@1,stripe_elements@2,airwallex_dropin@1'

function requireData<T>(response: {
  data?: T
  error?: { message: string; code?: string; status?: number }
}): T {
  if (response.error || response.data === undefined) {
    throw new ApiRequestError(
      response.error?.message || 'Checkout request failed',
      response.error?.code,
      response.error?.status
    )
  }
  return response.data
}

function mutationHeaders(sessionToken: string, idempotencyKey?: string) {
  const headers: Record<string, string> = {
    Authorization: `CheckoutSession ${sessionToken}`,
    'Checkout-Presentation-Accept': CHECKOUT_PRESENTATION_ACCEPT,
  }
  if (idempotencyKey) {
    headers['Idempotency-Key'] = idempotencyKey
  }
  return headers
}

function readHeaders(token: string, scope: 'mutation' | 'handoff') {
  return {
    Authorization:
      scope === 'mutation' ? `CheckoutSession ${token}` : `CheckoutResultHandoff ${token}`,
    'Checkout-Presentation-Accept': CHECKOUT_PRESENTATION_ACCEPT,
  }
}

function requireSupportedPresentation(execution: CheckoutExecution): CheckoutExecution {
  if (
    typeof execution.method_binding !== 'string' ||
    !/^sha256:[0-9a-f]{64}$/.test(execution.method_binding)
  ) {
    throw new ApiRequestError(
      'Checkout execution method binding is invalid',
      'CHECKOUT_ACTIVE_EXECUTION_METHOD_UNAVAILABLE'
    )
  }
  if (
    execution.payment_switch &&
    !['confirm_unpaid', 'purchase_successor', 'blocked'].includes(execution.payment_switch.mode)
  ) {
    throw new ApiRequestError(
      'Checkout payment switch policy is unsupported',
      'CHECKOUT_PAYMENT_SWITCH_BLOCKED'
    )
  }
  const presentation = execution.presentation
  if (!presentation) {
    return execution
  }
  if (
    (presentation.flow === 'stripe_elements' && presentation.version !== 2) ||
    (presentation.flow !== 'stripe_elements' && presentation.version !== 1)
  ) {
    throw new ApiRequestError(
      'Checkout presentation version is unsupported',
      'CHECKOUT_PRESENTATION_UNSUPPORTED'
    )
  }
  if (
    (presentation.flow === 'hosted_redirect' && presentation.mode === 'hosted_redirect') ||
    (presentation.flow === 'airwallex_dropin' && presentation.mode === 'embedded')
  ) {
    return execution
  }

  if (
    presentation.flow === 'stripe_elements' &&
    presentation.mode === 'embedded' &&
    typeof presentation.payload?.client_secret === 'string' &&
    presentation.payload.client_secret.trim()
  ) {
    return execution
  }
  throw new ApiRequestError(
    'Checkout presentation is unsupported',
    'CHECKOUT_PRESENTATION_UNSUPPORTED'
  )
}

function majorAmountToMinorUnits(amount: string, currency: string): number | null {
  const normalizedAmount = amount.trim()
  const normalizedCurrency = currency.trim().toUpperCase()
  if (!/^\d+(?:\.\d+)?$/.test(normalizedAmount) || normalizedCurrency.length !== 3) {
    return null
  }

  let fractionDigits: number
  try {
    const resolvedFractionDigits = new Intl.NumberFormat('en', {
      style: 'currency',
      currency: normalizedCurrency,
    }).resolvedOptions().maximumFractionDigits
    if (typeof resolvedFractionDigits !== 'number') {
      return null
    }
    fractionDigits = resolvedFractionDigits
  } catch {
    return null
  }

  const amountParts = normalizedAmount.split('.')
  const whole = amountParts[0]
  const fraction = amountParts[1] ?? ''
  if (!whole) {
    return null
  }
  const significantFraction = fraction.slice(0, fractionDigits)
  if (/[^0]/.test(fraction.slice(fractionDigits))) {
    return null
  }
  const scale = 10n ** BigInt(fractionDigits)
  const minorUnits =
    BigInt(whole) * scale + BigInt(significantFraction.padEnd(fractionDigits, '0') || '0')
  return minorUnits <= BigInt(Number.MAX_SAFE_INTEGER) ? Number(minorUnits) : null
}

function requireSupportedMethods(response: CheckoutPaymentMethodsResponse) {
  for (const group of response.groups) {
    for (const method of group.methods) {
      if (
        typeof method.method_binding !== 'string' ||
        !/^sha256:[0-9a-f]{64}$/.test(method.method_binding) ||
        (method.presentation_flow === 'stripe_elements'
          ? method.presentation_version !== 2
          : method.presentation_version !== 1) ||
        !['hosted_redirect', 'stripe_elements', 'airwallex_dropin'].includes(
          method.presentation_flow
        )
      ) {
        throw new ApiRequestError(
          'Checkout payment method presentation is unsupported',
          'CHECKOUT_PRESENTATION_UNSUPPORTED'
        )
      }
      if (!response.selection_only) requireSupportedMethodPresentation(method)
    }
  }
  return response
}

function requireSupportedMethodPresentation(method: CheckoutMethod): void {
  if (method.presentation_flow !== 'stripe_elements') {
    if (method.presentation !== null) {
      throw new ApiRequestError(
        'Checkout payment method presentation is unsupported',
        'CHECKOUT_PRESENTATION_UNSUPPORTED'
      )
    }
    return
  }
  const presentation = method.presentation
  const payload = presentation?.payload
  const methodCurrency = method.currency?.trim().toUpperCase()
  const payloadCurrency = payload?.currency?.trim().toUpperCase()
  const expectedMinorAmount =
    typeof method.amount === 'string' && typeof methodCurrency === 'string'
      ? majorAmountToMinorUnits(method.amount, methodCurrency)
      : null
  if (
    method.presentation_mode !== 'embedded' ||
    !method.requires_provider_client_payload ||
    presentation?.mode !== 'embedded' ||
    presentation.flow !== 'stripe_elements' ||
    presentation.version !== 2 ||
    !payload ||
    typeof payload.publishable_key !== 'string' ||
    !payload.publishable_key.trim() ||
    !['payment', 'subscription'].includes(payload.elements_mode) ||
    payload.payment_method_creation !== 'manual' ||
    !Number.isSafeInteger(payload.amount) ||
    payload.amount <= 0 ||
    typeof payload.currency !== 'string' ||
    payload.currency.trim().length !== 3 ||
    payloadCurrency !== methodCurrency ||
    expectedMinorAmount === null ||
    payload.amount !== expectedMinorAmount ||
    !['apple_pay', 'google_pay'].includes(payload.payment_method) ||
    payload.payment_method !== method.pm_id ||
    !Array.isArray(payload.payment_method_types) ||
    payload.payment_method_types.length !== 1 ||
    payload.payment_method_types[0] !== 'card'
  ) {
    throw new ApiRequestError(
      'Checkout payment method presentation is unsupported',
      'CHECKOUT_PRESENTATION_UNSUPPORTED'
    )
  }
}

export async function exchangeCheckoutAuthorization(
  sessionNo: string,
  authorizationCode: string,
  idempotencyKey: string
): Promise<CheckoutAuthorizationExchangeResponse> {
  const response = await getApiClient().post<CheckoutAuthorizationExchangeResponse>(
    `/api/v1/checkout-sessions/${encodeURIComponent(sessionNo)}/authorizations/exchange`,
    { authorization_code: authorizationCode },
    { headers: { 'Idempotency-Key': idempotencyKey } }
  )
  return requireData(response)
}

export async function getCheckoutSession(
  sessionNo: string,
  token: string,
  scope: 'mutation' | 'handoff',
  countryCode?: string
): Promise<CheckoutSession> {
  const params = new URLSearchParams({ channel: 'web' })
  if (countryCode) {
    params.set('country_code', countryCode)
  }
  const response = await getApiClient().get<CheckoutSession>(
    `/api/v1/checkout-sessions/${encodeURIComponent(sessionNo)}?${params.toString()}`,
    { headers: readHeaders(token, scope) }
  )
  const session = requireData(response)
  if (session.active_execution) {
    requireSupportedPresentation(session.active_execution)
  }
  return session
}

export async function getCheckoutPaymentMethods(
  sessionNo: string,
  sessionToken: string,
  countryCode: string,
  locale: string,
  selectionOnly = false
): Promise<CheckoutPaymentMethodsResponse> {
  const params = new URLSearchParams({ country_code: countryCode, channel: 'web' })
  if (selectionOnly) params.set('selection_only', 'true')
  const response = await getApiClient().get<CheckoutPaymentMethodsResponse>(
    `/api/v1/checkout-sessions/${encodeURIComponent(sessionNo)}/payment-methods?${params.toString()}`,
    {
      headers: {
        ...mutationHeaders(sessionToken),
        'Accept-Language': locale,
      },
    }
  )
  const methods = requireData(response)
  if ((methods.selection_only ?? false) !== selectionOnly)
    throw new ApiRequestError(
      'Checkout payment method scope changed',
      'CHECKOUT_PAYMENT_SWITCH_STATE_CHANGED'
    )
  return requireSupportedMethods(methods)
}

export async function startCheckoutExecution(
  sessionNo: string,
  sessionToken: string,
  idempotencyKey: string,
  request: CheckoutStartExecutionRequest
): Promise<CheckoutExecution> {
  const response = await getApiClient().post<CheckoutExecution>(
    `/api/v1/checkout-sessions/${encodeURIComponent(sessionNo)}/executions`,
    request,
    { headers: mutationHeaders(sessionToken, idempotencyKey) }
  )
  return requireSupportedPresentation(requireData(response))
}

export async function createCheckoutPurchaseSuccessor(
  sessionNo: string,
  sessionToken: string,
  idempotencyKey: string,
  request: CheckoutPurchaseSuccessorRequest
): Promise<CheckoutPurchaseSuccessorResponse> {
  const response = await getApiClient().post<CheckoutPurchaseSuccessorResponse>(
    `/api/v1/checkout-sessions/${encodeURIComponent(sessionNo)}/purchase-successors`,
    request,
    { headers: mutationHeaders(sessionToken, idempotencyKey) }
  )
  return requireData(response)
}

export async function getCheckoutExecution(
  sessionNo: string,
  executionNo: string,
  token: string,
  scope: 'mutation' | 'handoff'
): Promise<CheckoutExecution> {
  const response = await getApiClient().get<CheckoutExecution>(
    `/api/v1/checkout-sessions/${encodeURIComponent(sessionNo)}/executions/${encodeURIComponent(executionNo)}`,
    { headers: readHeaders(token, scope) }
  )
  return requireSupportedPresentation(requireData(response))
}

export async function reconcileCheckoutExecutionPayment(
  sessionNo: string,
  executionNo: string,
  token: string,
  scope: 'mutation' | 'handoff',
  idempotencyKey: string,
  verifyApprovalLink = false
): Promise<CheckoutExecution> {
  const response = await getApiClient().post<CheckoutExecution>(
    `/api/v1/checkout-sessions/${encodeURIComponent(sessionNo)}/executions/${encodeURIComponent(executionNo)}/reconcile${verifyApprovalLink ? '?verify_approval_link=true' : ''}`,
    {},
    {
      headers: {
        ...readHeaders(token, scope),
        'Idempotency-Key': idempotencyKey,
      },
    }
  )
  return requireSupportedPresentation(requireData(response))
}

export async function cancelCheckoutExecution(
  sessionNo: string,
  executionNo: string,
  sessionToken: string,
  idempotencyKey: string
): Promise<CheckoutExecutionCancelResponse> {
  const response = await getApiClient().post<CheckoutExecutionCancelResponse>(
    `/api/v1/checkout-sessions/${encodeURIComponent(sessionNo)}/executions/${encodeURIComponent(executionNo)}/cancel`,
    {},
    { headers: mutationHeaders(sessionToken, idempotencyKey) }
  )
  return requireData(response)
}

export async function cancelCheckoutSession(
  sessionNo: string,
  sessionToken: string,
  idempotencyKey: string
): Promise<CheckoutSessionCancelResponse> {
  const response = await getApiClient().post<CheckoutSessionCancelResponse>(
    `/api/v1/checkout-sessions/${encodeURIComponent(sessionNo)}/cancel`,
    {},
    { headers: mutationHeaders(sessionToken, idempotencyKey) }
  )
  return requireData(response)
}

export async function reviseCheckoutSession(
  sessionNo: string,
  sessionToken: string,
  idempotencyKey: string,
  request: CheckoutSessionRevisionRequest
): Promise<CheckoutSessionRevisionResponse> {
  const response = await getApiClient().patch<CheckoutSessionRevisionResponse>(
    `/api/v1/checkout-sessions/${encodeURIComponent(sessionNo)}`,
    request,
    { headers: mutationHeaders(sessionToken, idempotencyKey) }
  )
  return requireData(response)
}

export async function completeCheckoutSession(
  sessionNo: string,
  sessionToken: string,
  idempotencyKey: string,
  expectedRevision: number
): Promise<CheckoutSessionCompleteResponse> {
  const response = await getApiClient().post<CheckoutSessionCompleteResponse>(
    `/api/v1/checkout-sessions/${encodeURIComponent(sessionNo)}/complete`,
    { expected_revision: expectedRevision },
    { headers: mutationHeaders(sessionToken, idempotencyKey) }
  )
  return requireData(response)
}

export async function issueCheckoutResultAuthorization(
  sessionNo: string,
  resultHandoffToken: string,
  idempotencyKey: string,
  expectedRevision: number
): Promise<CheckoutResultAuthorizationResponse> {
  const response = await getApiClient().post<CheckoutResultAuthorizationResponse>(
    `/api/v1/checkout-sessions/${encodeURIComponent(sessionNo)}/result-authorizations`,
    { expected_revision: expectedRevision },
    {
      headers: {
        ...readHeaders(resultHandoffToken, 'handoff'),
        'Idempotency-Key': idempotencyKey,
      },
    }
  )
  return requireData(response)
}

export async function exchangeCheckoutResultAuthorization(
  sessionNo: string,
  authorizationCode: string,
  idempotencyKey: string
): Promise<CheckoutResultAuthorizationExchangeResponse> {
  const response = await getApiClient().post<CheckoutResultAuthorizationExchangeResponse>(
    `/api/v1/checkout-results/${encodeURIComponent(sessionNo)}/authorizations/exchange`,
    { authorization_code: authorizationCode },
    { headers: { 'Idempotency-Key': idempotencyKey } }
  )
  return requireData(response)
}

export async function getProductCheckoutResult(
  sessionNo: string,
  resultToken?: string
): Promise<ProductCheckoutResultResponse> {
  const response = await getApiClient().get<ProductCheckoutResultResponse>(
    `/api/v1/checkout-results/${encodeURIComponent(sessionNo)}`,
    resultToken ? { headers: { Authorization: `CheckoutResult ${resultToken}` } } : undefined
  )
  return requireData(response)
}

export async function restartCheckoutSession(
  sessionNo: string,
  sessionToken: string,
  idempotencyKey: string,
  request: CheckoutRestartRequest
): Promise<CheckoutRestartResponse> {
  return requireData(
    await getApiClient().post<CheckoutRestartResponse>(
      `/api/v1/checkout-sessions/${encodeURIComponent(sessionNo)}/restart`,
      request,
      { headers: mutationHeaders(sessionToken, idempotencyKey) }
    )
  )
}
